import React, { useState, useMemo } from "react";
import {
  Table, TableRow, TableCell, TableHead, TableBody,
  Typography, TablePagination, Box, IconButton,
  TableContainer, Paper, Tooltip, Select,
  MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { useEffectAsync } from "../reactHelper";
import { getWithExpiry } from "../common/util/localstorage";
import SearchHeader from "../common/components/SearchHeader";
import { useDispatch, useSelector } from "react-redux";
import { presencesActions } from "../store";
import { useNavigate } from "react-router-dom";

import ErrorDialog from "../common/components/ErrorDialog";
import CollectionFab from "../settings/CollectionFab";
import CollectionActions from "../common/components/CollectionActions";

/* -------------------- COMPONENT -------------------- */

const PresenceReportsPage = () => {
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selected, setSelected] = useState(null);

  const [opening, setOpening] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const _reports = useSelector((state) => state.presences?.items || []);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ---------------- FETCH ---------------- */
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_URL_NAME}/presence`,
        {
          headers: {
            Authorization: `Bearer ${getWithExpiry("TOKEN")}`,
          },
        }
      );

      if (!res.ok) {
        if (res.status === 401) navigate("/login");
        return;
      }

      const data = await res.json();
      dispatch(presencesActions.setItems(data));
    } finally {
      setLoading(false);
    }
  }, []);

  /* ---------------- FILTER ---------------- */
  const months = ["all", ...new Set(_reports.map((r) => r.mois))];

  const filtered = _reports
    .filter((r) =>
      monthFilter === "all" ? true : r.mois === monthFilter
    )
    .filter((r) =>
      r.mois.toLowerCase().includes(searchKeyword.toLowerCase())
    );

  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* ---------------- PDF EXPORT ---------------- */
  const exportPDF = () => {
    const doc = new jsPDF("landscape");

    doc.setFontSize(14);
    doc.text("Rapport Présence", 14, 15);

    const rows = _reports.flatMap((r) =>
      (r.listPresence || []).map((p) => [
        r.mois,
        p.employee,
        Number(p.workedHours || 0).toFixed(2),
        Number(p.expectedHours || 0).toFixed(2),
        (Number(p.workedHours || 0) - Number(p.expectedHours || 0)).toFixed(2),
      ])
    );

    autoTable(doc, {
      startY: 25,
      head: [[
        "Mois",
        "Employé",
        "Travail (h)",
        "Normal (h)",
        "Diff"
      ]],
      body: rows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [0, 35, 85] },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });

    doc.save("presence_all_employees.pdf");
  };

  /* ---------------- RENDER ---------------- */
  return (
    <>
      <Typography variant="h3" align="center" sx={{ mb: 3 }}>
        Presence Reports
      </Typography>

      {/* FILTER */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        <SearchHeader keyword={searchKeyword} setKeyword={setSearchKeyword} />

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Mois</InputLabel>
          <Select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
          >
            {months.map((m, i) => (
              <MenuItem key={i} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Export PDF">
          <IconButton onClick={exportPDF} color="error">
            <PictureAsPdfIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* TABLE */}
      <TableContainer component={Paper}>
        <Table>

          <TableHead>
            <TableRow sx={{ backgroundColor: "#002355" }}>
              <TableCell sx={{ color: "#fff" }}>Mois</TableCell>
              <TableCell sx={{ color: "#fff" }}>Note</TableCell>
              <TableCell sx={{ color: "#fff" }}>View</TableCell>
              <TableCell sx={{ color: "#fff" }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && paginated.length ? (
              paginated.map((r) => (
                <TableRow key={r._id} hover>

                  <TableCell>{r.mois}</TableCell>
                  <TableCell>{r.note || "-"}</TableCell>

                  <TableCell>
                    <IconButton onClick={() => setSelected(r)}>
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>

                  <TableCell padding="none">
                    <CollectionActions
                      itemId={r._id}
                      editPath="/settings/presenceReport"
                      endpoint="presence"
                      action={presencesActions}
                      removable
                    />
                  </TableCell>

                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4}>Loading...</TableCell>
              </TableRow>
            )}
          </TableBody>

        </Table>
      </TableContainer>

      {/* PAGINATION */}
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(+e.target.value);
          setPage(0);
        }}
      />

      {/* MODAL */}
      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Presence - {selected?.mois}
        </DialogTitle>

        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Worked</TableCell>
                <TableCell>Expected</TableCell>
                <TableCell>Diff</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {selected?.listPresence?.map((p) => (
                <TableRow key={p._id}>
                  <TableCell>{p.employee}</TableCell>

                  <TableCell>
                    {Number(p.workedHours || 0).toFixed(2)}
                  </TableCell>

                  <TableCell>
                    {Number(p.expectedHours || 0).toFixed(2)}
                  </TableCell>

                  <TableCell>
                    {(Number(p.workedHours || 0) - Number(p.expectedHours || 0)).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      <CollectionFab editPath="/settings/presenceReport" />
      <ErrorDialog open={opening} errorMsg={errorMsg} />
    </>
  );
};

export default PresenceReportsPage;